import { SafeAppConnector } from '@gnosis.pm/safe-apps-web3-react';
import Eth from '@ledgerhq/hw-app-eth';
import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core';
import { NoEthereumProviderError } from '@web3-react/injected-connector';
import { NoEthereumProviderError as NaboxNoEthereumProviderError } from '../../web3/NaboxConnector';
import { BitkeepNoEthereumProviderError } from '../../web3/BitkeepConnector';
import { NoEthereumProviderError as OKXNoEthereumProviderError } from '../../web3/OKXConnector';
import {
    UserRejectedRequestError,
    WalletConnectConnector,
} from '@web3-react/walletconnect-connector';
import React, { ReactNode, useCallback, useContext, useEffect, useReducer } from 'react';
import { SupportedChains, SupportedConnectors } from '../../config/constants';
import useIsMounted from '../../hooks/useIsMounted';
import useAutoConnect from '../../hooks/web3/useAutoConnect';
import { injected, networkConnector } from '../../web3/connectors';
import modalContext from '../modal/modalContext';
import ConnectionContext from './connectionContext';
import connectionReducer from './connectionReducer';

const ConnectionState: React.FC<ReactNode> = ({ children }) => {
    const { error, connector, chainId, activate, deactivate, setError } = useWeb3React();

    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const isMounted = useIsMounted();

    const resetWalletConnector = useCallback((connector: any) => {
        if (
            connector &&
            connector instanceof WalletConnectConnector &&
            connector.walletConnectProvider?.connector?.uri
        ) {
            connector.walletConnectProvider = undefined;
        }
    }, []);

    const activateWallet = useCallback(
        async (name: SupportedConnectors, connector: any) => {
            try {
                await activate(connector, undefined, true);
                dispatch({
                    type: 'SET_WALLET_CONNECTION',
                    payload: {
                        name,
                        connector,
                    },
                });
                closeModal('connectWalletModal');
            } catch (error: any) {
                setError(error);
                resetWalletConnector(connector);
            }
        },
        [activate, closeModal, setError, resetWalletConnector]
    );

    const disconnectWallet = () => {
        dispatch({
            type: 'DISCONNECT_WALLET',
        });
        deactivate();
    };

    const setLedgerTransportAndResolve = (eth: Eth, resolve: any) =>
        dispatch({
            type: 'SET_LEDGER_TRANSPORT_AND_RESOLVE',
            payload: {
                eth,
                resolve,
            },
        });

    const handleDisconnect = async () => {
        await disconnectWallet();
        activeConnector.connector.close();

        const isAuthorized = await injected.isAuthorized();

        if (isAuthorized) {
            activateWallet(SupportedConnectors.METAMASK, injected);
            return;
        }
        activate(networkConnector);
    };

    const initialState = {
        activeConnector: {
            connector: null,
            name: null,
        },
        ledgerTransportAndResolve: {
            eth: null,
            resolve: null,
        },
        activateWallet,
        disconnectWallet,
        handleDisconnect,
        setLedgerTransportAndResolve,
    };

    const [state, dispatch] = useReducer(connectionReducer, initialState);

    const { activeConnector, ledgerTransportAndResolve } = state;

    useEffect(() => {
        (async () => {
            if (
                connector &&
                connector instanceof SafeAppConnector &&
                (await connector.isSafeApp()) &&
                chainId &&
                chainId !== SupportedChains.MAINNET &&
                chainId !== SupportedChains.ARBITRUM_ONE
            ) {
                isMounted() &&
                    setError(
                        new UnsupportedChainIdError(chainId, [SupportedChains.MAINNET])
                    );
            }
        })();
    }, [connector, chainId, setError, isMounted]);

    useEffect(() => {
        if (!error) return;

        switch (true) {
            case error instanceof UnsupportedChainIdError:
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'Please connect to Ethereum Mainnet/Arbitrum',
                }));
                break;
            case error instanceof NoEthereumProviderError:
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'No ethereum provider found',
                }));
                break;
            case error instanceof NaboxNoEthereumProviderError:
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: `Please install <a href='https://nabox.io/' target='_blank' rel='noreferrer noopener'><u>Nabox Wallet</u></a>`,
                }));
                break;
            case error instanceof BitkeepNoEthereumProviderError:
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: `Please install <a href='https://bitkeep.com/en/download?type=2' target='_blank' rel='noreferrer noopener'><u>Bitkeep Wallet</u></a>`,
                }));
                break;
            case error instanceof OKXNoEthereumProviderError:
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: `Please install <a href='https://www.okx.com/download?channelId=GoogleSEO'><u>OKX Wallet</u></a>`,
                }));
                break;
            case error.name === 'TransportError':
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'U2F browser support is required',
                }));
                break;
            case error.message === 'Transport is missing':
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'Trezor Bridge not installed',
                }));
                break;
            case error.name === 'TransportStatusError':
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message:
                        'Make sure your Ledger device is connected OR account is selected',
                }));
                break;
            // case error === 'User denied login.':
            case error instanceof UserRejectedRequestError:
            case error.message === 'User cancelled login':
                return;
            default:
                console.log(error);
        }

        closeModal('connectWalletModal');
    }, [error, closeModal, openModal, setModalData]);

    return (
        <ConnectionContext.Provider
            value={{
                activeConnector,
                ledgerTransportAndResolve,
                activateWallet,
                disconnectWallet,
                handleDisconnect,
                setLedgerTransportAndResolve,
            }}
        >
            <AttempInitializeConnection />
            {children}
        </ConnectionContext.Provider>
    );
};

export default ConnectionState;

const AttempInitializeConnection = () => {
    const { active, activate } = useWeb3React();

    const tried = useAutoConnect();

    useEffect(() => {
        if (tried && !active) {
            activate(networkConnector);
        }
    }, [tried, active, activate]);

    return null;
};
